// ContactsPage — Phase 1 cleanup — client contacts CRUD.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, X, Save, Mail, Phone, UserCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";

// CAN_EDIT_ROLES: roles with contacts='E' per RBAC matrix.
// director/administrator/consultant have E; client_admin is V (read-only); site_manager is - (no access).
const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}

export default function ContactsPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [clientFilter, setClientFilter] = useState("All");
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const [searchParams, setSearchParams] = useSearchParams();

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (portalClientId && !isInternalView) params.client_id = portalClientId;
            else if (clientFilter !== "All") params.client_id = clientFilter;
            const { data } = await api.get("/contacts", { params });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load contacts");
        }
        setLoading(false);
    }, [portalClientId, isInternalView, clientFilter]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => { }); }, []);
    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId && rows.length) {
            const r = rows.find((x) => x.id === openId);
            if (r && canEdit) setDialog(r);
            searchParams.delete("open"); setSearchParams(searchParams, { replace: true });
        }
    }, [rows, canEdit, searchParams, setSearchParams]);

    const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const filtered = rows.filter((r) => {
        if (!q) return true;
        const hay = `${r.name} ${r.role || ""} ${r.email || ""} ${r.phone || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
    });
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, {
        accessors: {
            name: (r) => r.name,
            client: (r) => clientMap[r.client_id]?.name || "",
            department: (r) => r.department || "",
            email: (r) => r.email || "",
            phone: (r) => r.phone || "",
            portal_access: (r) => r.portal_access,
        },
    });

    async function deleteContact(c) {
        if (!window.confirm(`Delete contact ${c.name}?`)) return;
        try {
            await api.delete(`/contacts/${c.id}`);
            toast.success("Contact removed");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    }

    return (
        <div data-testid="contacts-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Clients &amp; Portals</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Contacts</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} contacts · {rows.filter((r) => r.portal_access).length} with portal access · {rows.filter((r) => r.is_primary).length} primary
                    </p>
                </div>
                {canEdit && (
                    <button data-testid="contact-new-btn" onClick={() => setDialog("new")}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus size={15} /> Add contact
                    </button>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968F]" />
                    <input data-testid="contact-search" placeholder="Search name, role, email, phone…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-80 focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                </div>
                {isInternalView && (
                    <select data-testid="contact-client-filter" value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="All">All clients</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                        <tr>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="name" onClick={toggleSort}>Contact</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="client" onClick={toggleSort}>Client</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="department" onClick={toggleSort}>Department</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="email" onClick={toggleSort}>Email</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="phone" onClick={toggleSort}>Phone</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="portal_access" onClick={toggleSort}>Portal</SortableTh>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]">
                                <Loader2 size={16} className="inline animate-spin mr-2" />Loading…</td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]">No contacts.</td></tr>
                        ) : sorted.map((r) => (
                            <tr key={r.id} data-testid={`contact-row-${r.id}`}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                onClick={() => canEdit && setDialog(r)}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-[#1F1F1F]">{r.name}
                                        {r.is_primary && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#FBEFE5] text-[#9F4212]">Primary</span>}
                                    </div>
                                    <div className="text-[11px] text-[#9A968F]">{r.role || "—"}</div>
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{clientMap[r.client_id]?.name || "—"}</td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.department || "—"}</td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">
                                    {r.email ? <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()}
                                        className="text-[#2A5DB0] hover:underline inline-flex items-center gap-1"><Mail size={11} />{r.email}</a> : "—"}
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">
                                    {r.phone ? <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()}
                                        className="text-[#2A5DB0] hover:underline inline-flex items-center gap-1"><Phone size={11} />{r.phone}</a> : "—"}
                                </td>
                                <td className="px-4 py-3">{r.portal_access && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#3F7D3A]"><UserCheck size={12} /> Yes</span>}</td>
                                <td className="px-4 py-3 text-right">
                                    {canEdit && (
                                        <button onClick={(e) => { e.stopPropagation(); deleteContact(r); }}
                                            data-testid={`contact-delete-${r.id}`}
                                            className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={13} /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <ContactDialog
                    contact={dialog === "new" ? null : dialog}
                    clients={clients}
                    portalClientId={!isInternalView ? portalClientId : null}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); refresh(); }}
                />
            )}
        </div>
    );
}

function ContactDialog({ contact, clients, portalClientId, onClose, onSaved }) {
    const isNew = !contact;
    const [form, setForm] = useState(() => contact ? { ...contact } : {
        client_id: portalClientId || clients[0]?.id || "",
        name: "", role: "", email: "", phone: "", department: "",
        notes: "", portal_access: false, is_primary: false,
    });
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!form.client_id) { toast.error("Client required"); return; }
        if (!(form.name || "").trim()) { toast.error("Name required"); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            ["role", "email", "phone", "department", "notes"].forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            if (isNew) {
                await api.post("/contacts", payload);
                toast.success("Contact added");
            } else {
                await api.patch(`/contacts/${contact.id}`, payload);
                toast.success("Contact updated");
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
        setSaving(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="contact-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">{isNew ? "Add contact" : `Edit ${contact.name}`}</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Client *">
                        <select data-testid="contact-client" value={form.client_id}
                            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                            disabled={!!portalClientId || !isNew} className={inputCls}>
                            <option value="">— Select —</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Name *">
                            <input data-testid="contact-name" value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Role / title">
                            <input value={form.role || ""}
                                onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Email">
                            <input type="email" value={form.email || ""}
                                onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Phone">
                            <input value={form.phone || ""}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Department">
                            <input value={form.department || ""}
                                onChange={(e) => setForm({ ...form, department: e.target.value })} className={inputCls} />
                        </Field>
                    </div>
                    <Field label="Notes">
                        <textarea rows={2} value={form.notes || ""}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className={inputCls + " h-auto py-2"} />
                    </Field>
                    <div className="flex items-center gap-6 pt-1">
                        <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                            <input data-testid="contact-portal-access" type="checkbox" checked={!!form.portal_access}
                                onChange={(e) => setForm({ ...form, portal_access: e.target.checked })} />
                            Has portal login access
                        </label>
                        <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                            <input type="checkbox" checked={!!form.is_primary}
                                onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
                            Primary contact
                        </label>
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="contact-save" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isNew ? "Add" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
